import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Checkbox, Dropdown, Empty, Menu, Spin, message as notification } from 'antd'
import { RootState } from '../../store/store'
import { GuestChat, GuestChoice, GuestMessage, GuestStart, guestChatAPI } from '../../api/guest-chat'
import { Button, Form, Input, Link, Modal, Select } from '../common'
import { Option } from '../common/select/select'
import Avatar from '../common/avatar/avatar'
import { BackwardIcon, Close, More } from '../common/icons'
import { readGuestDraft, saveGuestDraft } from './guest-chat-draft'
import styles from './guest-chat.module.scss'

type Details = Omit<GuestStart, 'message' | 'clientId' | 'captchaKey' | 'captchaValue'>
const makeId = () => `guest:${Date.now()}:${Math.random().toString(36).slice(2)}`
const greeting = 'Здравствуйте! Главные эксперты ответят на вопросы о компетенциях, заданиях и правилах чемпионата.'
const HeadsetIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 18 0v6.6a2.4 2.4 0 0 1-2.4 2.4H17a1.5 1.5 0 0 1-1.5-1.5v-4.8a1.5 1.5 0 0 1 1.5-1.5h2.2V12a7.2 7.2 0 0 0-14.4 0v1.2H7a1.5 1.5 0 0 1 1.5 1.5v4.8A1.5 1.5 0 0 1 7 21H5.4A2.4 2.4 0 0 1 3 18.6V12Z" />
  </svg>
)
const ChevronIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path d="m4 6 4 4 4-4" />
  </svg>
)
const messageTime = (created: string, previous?: string) => {
  const date = new Date(created)
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  return !previous || date.toDateString() !== new Date(previous).toDateString()
    ? `${date.toLocaleDateString('ru-RU')} · ${time}`
    : time
}
const invitationDismissed = () => {
  try {
    return sessionStorage.getItem('guest-chat-invitation') === 'dismissed'
  } catch {
    return false
  }
}

const GuestChatWidget = () => {
  const userId = useSelector((state: RootState) => state.account.user?.id)
  const [open, setOpen] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [invitation, setInvitation] = useState(() => !invitationDismissed())
  const [chats, setChats] = useState<GuestChat[]>([])
  const [choices, setChoices] = useState<GuestChoice[]>([])
  const [active, setActive] = useState<number>()
  const [screen, setScreen] = useState<'list' | 'form' | 'chat'>('form')
  const [details, setDetails] = useState<Details>()
  const [items, setItems] = useState<GuestMessage[]>([])
  const [pins, setPins] = useState<GuestMessage[]>([])
  const [older, setOlder] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [captchaVisible, setCaptchaVisible] = useState(false)
  const [captchaValue, setCaptchaValue] = useState('')
  const [greetingAt, setGreetingAt] = useState(() => new Date().toISOString())
  const [busy, setBusy] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [error, setError] = useState('')
  const [connected, setConnected] = useState(true)
  const [claimable, setClaimable] = useState<GuestChat[]>([])
  const [claimDismissed, setClaimDismissed] = useState<number>()
  const [socketRevision, setSocketRevision] = useState(0)
  const [bottom, setBottom] = useState(20)
  const [availableHeight, setAvailableHeight] = useState(Math.max(180, window.innerHeight - 32))
  const [competence, setCompetence] = useState<string>()
  const [form] = Form.useForm()
  const [captchaForm] = Form.useForm()
  const scrollRef = useRef<HTMLDivElement>(null)
  const version = useRef(0)
  const alive = useRef(true)
  const pending = useRef<{ text: string; id: string }>()
  const loadedCount = useRef(0)
  loadedCount.current = items.length
  const current = useRef({ active, open, screen, userId })
  current.current = { active, open, screen, userId }

  useEffect(() => setActionsOpen(false), [open, screen, active])

  useEffect(() => {
    setCaptchaVisible(false)
    setCaptchaValue('')
  }, [active, details, screen])

  useEffect(() => {
    if (active || !details || userId) return
    if (!text.trim()) {
      saveGuestDraft()
      return
    }
    if (!pending.current || pending.current.text !== text) pending.current = { text, id: makeId() }
    saveGuestDraft({ details, ...pending.current })
  }, [text, details, active, userId])

  useEffect(() => {
    if (!invitation) {
      try {
        sessionStorage.setItem('guest-chat-invitation', 'dismissed')
      } catch {
        /* Browser storage can be disabled; chat access uses a cookie. */
      }
    }
  }, [invitation])

  useEffect(() => {
    if (!userId) setClaimDismissed(undefined)
  }, [userId])

  const refresh = useCallback(async () => {
    const list = await guestChatAPI.session()
    if (alive.current) setChats(list)
    return list
  }, [])

  const recover = useCallback(async () => {
    const chat = await guestChatAPI.recover()
    if (!chat) return
    await refresh()
    if (current.current.userId) setClaimable(await guestChatAPI.claimable())
    else {
      setActive(chat.id)
      setScreen('chat')
      setOpen(true)
      setSocketRevision((value) => value + 1)
    }
  }, [refresh])

  useEffect(() => {
    const restore = () => {
      void refresh()
        .then(recover)
        .catch(() => setError('Не удалось восстановить чат. Проверьте ссылку и повторите попытку.'))
    }
    window.addEventListener('guest-chat.recover', restore)
    return () => window.removeEventListener('guest-chat.recover', restore)
  }, [refresh, recover])

  useEffect(() => {
    alive.current = true
    let stopped = false
    const initialize = async () => {
      try {
        const list = await refresh()
        const recovered = await guestChatAPI.recover()
        if (stopped) return
        if (recovered) {
          await refresh()
          setActive(recovered.id)
          setScreen('chat')
          setOpen(true)
        } else if (!current.current.active) {
          const draft = !userId ? readGuestDraft() : undefined
          if (draft) {
            const existing = list.find((chat) => chat.expert.id === draft.details.expert && !chat.isClosed)
            pending.current = { text: draft.text, id: draft.id }
            setDetails(draft.details)
            setText(draft.text)
            setActive(existing?.id)
            setScreen('chat')
            setOpen(true)
          } else if (list.length) setScreen('list')
        }
        if (userId) {
          const transferable = await guestChatAPI.claimable()
          if (!stopped) setClaimable(transferable)
        } else {
          const available = await guestChatAPI.choices()
          if (!stopped) setChoices(available)
        }
      } catch {
        if (!stopped) setError('Не удалось загрузить чаты. Проверьте соединение и повторите попытку.')
      }
    }
    void initialize()
    return () => {
      stopped = true
      alive.current = false
    }
  }, [userId, refresh])

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const selection = (event as CustomEvent<{ competence: number; expert: number }>).detail
      const existing = chats.find((chat) => chat.expert.id === selection.expert && !chat.isClosed)
      setOpen(true)
      setInvitation(false)
      setError('')
      if (existing) {
        setActive(existing.id)
        setScreen('chat')
      } else {
        setActive(undefined)
        setDetails(undefined)
        setScreen('form')
        setCompetence(String(selection.competence))
        form.setFieldsValue({
          competence: String(selection.competence),
          expert: String(selection.expert),
          ...(chats[0]?.name ? { name: chats[0].name } : {}),
        })
      }
    }
    window.addEventListener('guest-chat.open', handleOpen)
    return () => window.removeEventListener('guest-chat.open', handleOpen)
  }, [chats, form])

  useEffect(() => {
    const measure = () => {
      const height = window.visualViewport?.height || window.innerHeight
      const offset = window.visualViewport?.offsetTop || 0
      const keyboard = Math.max(0, window.innerHeight - height - offset)
      const margin = 20
      setAvailableHeight(Math.max(180, height - margin - 12))
      setBottom(keyboard + margin)
    }
    measure()
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('scroll', measure)
    return () => {
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('scroll', measure)
    }
  }, [])

  const load = useCallback(async (id: number, initial = false) => {
    const requestVersion = ++version.current
    if (initial) setHistoryLoading(true)
    try {
      const history = await guestChatAPI.messages(id)
      // Refresh the entire loaded window so older edits/deletions do not remain in memory.
      if (!initial) {
        while (history.hasOlder && history.olderCursor && history.results.length < loadedCount.current) {
          if (requestVersion !== version.current || current.current.active !== id) return
          const earlier = await guestChatAPI.messages(id, history.olderCursor)
          if (!earlier.results.length) break
          history.results = [...earlier.results, ...history.results]
          history.olderCursor = earlier.olderCursor
          history.hasOlder = earlier.hasOlder
        }
      }
      if (requestVersion !== version.current || current.current.active !== id) return
      setItems(history.results)
      setPins(history.pinned)
      setOlder(history.hasOlder ? history.olderCursor : null)
      setError('')
    } catch {
      if (current.current.active === id) setError('Не удалось загрузить переписку. Возможно, срок доступа истек.')
    } finally {
      if (requestVersion === version.current) setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    version.current++
    setItems([])
    setPins([])
    setOlder(null)
    if (active && !userId && screen === 'chat') void load(active, true)
  }, [active, userId, screen, load])

  useEffect(() => {
    if (userId || !chats.length) return
    let stopped = false
    let socket: WebSocket | undefined
    let retry: ReturnType<typeof setTimeout>
    let failures = 0
    const resync = async () => {
      try {
        await refresh()
        const view = current.current
        if (view.active && view.open && view.screen === 'chat') await load(view.active)
      } catch {
        if (!stopped) setConnected(false)
      }
    }
    const connect = async () => {
      try {
        const { ticket } = await guestChatAPI.ticket()
        if (stopped) return
        const url = new URL(process.env.REACT_APP_ENDPOINT || window.location.origin, window.location.origin)
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
        url.pathname = '/ws/forum/guest/'
        url.search = new URLSearchParams({ ticket }).toString()
        socket = new WebSocket(url.toString())
        socket.onmessage = () => {
          failures = 0
          setConnected(true)
          void resync()
        }
        socket.onclose = () => {
          if (stopped) return
          setConnected(false)
          void resync()
          retry = setTimeout(connect, Math.min(30000, 1000 * 2 ** failures++))
        }
      } catch {
        if (!stopped) {
          setConnected(false)
          retry = setTimeout(connect, 10000)
        }
      }
    }
    const focus = () => {
      if (document.visibilityState === 'visible') void resync()
    }
    void connect()
    window.addEventListener('online', focus)
    document.addEventListener('visibilitychange', focus)
    return () => {
      stopped = true
      clearTimeout(retry)
      socket?.close()
      window.removeEventListener('online', focus)
      document.removeEventListener('visibilitychange', focus)
    }
  }, [userId, chats.length, socketRevision, refresh, load])

  const last = items[items.length - 1]?.id
  useEffect(() => {
    if (!active || !last || !open || screen !== 'chat' || userId) return
    const read = () => {
      if (document.visibilityState === 'visible') {
        void guestChatAPI
          .read(active, last)
          .then(refresh)
          .catch(() => undefined)
      }
    }
    read()
    document.addEventListener('visibilitychange', read)
    return () => document.removeEventListener('visibilitychange', read)
  }, [active, last, open, screen, userId, refresh])

  useEffect(() => {
    if (open && screen === 'chat') scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [last, open, screen])

  const start = (values: { name: string; competence: string; expert: string; consent: boolean }) => {
    const existing = chats.find((chat) => chat.expert.id === Number(values.expert) && !chat.isClosed)
    setActive(existing?.id)
    setDetails({ ...values, competence: Number(values.competence), expert: Number(values.expert) })
    setScreen('chat')
    setText('')
    setGreetingAt(new Date().toISOString())
    setError('')
  }
  const send = async () => {
    if (busy || !text.trim() || (!active && !details)) return
    if (!active && !captchaVisible) {
      setError('')
      setCaptchaVisible(true)
      return
    }
    if (!active && !captchaValue.trim()) return
    setBusy(true)
    setError('')
    try {
      const captcha = !active ? await captchaForm.validateFields() : {}
      if (!pending.current || pending.current.text !== text) pending.current = { text, id: makeId() }
      const attempt = pending.current
      if (active) {
        await guestChatAPI.send(active, text, attempt.id)
        await load(active)
      } else if (details) {
        const chat = await guestChatAPI.start({ ...details, ...captcha, message: text, clientId: attempt.id })
        setActive(chat.id)
        setSocketRevision((value) => value + 1)
      }
      pending.current = undefined
      saveGuestDraft()
      setText('')
      await refresh()
    } catch {
      setError('Не удалось отправить. Проверьте код с картинки, соединение и повторите попытку.')
      // A failed CAPTCHA is consumed server-side; remount to request a fresh one.
      if (!active) {
        setCaptchaValue('')
        captchaForm.setFieldsValue({ captchaKey: undefined, captchaValue: '' })
        setSocketRevision((value) => value + 1)
      }
    } finally {
      setBusy(false)
    }
  }
  const copyLink = async () => {
    if (!active) return
    try {
      const { token } = await guestChatAPI.link(active)
      const url = `${window.location.origin}/#guestChat=${encodeURIComponent(token)}`
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url)
      else Modal.info({ title: 'Ссылка для восстановления', content: <Input readOnly value={url} /> })
      notification.success('Ссылка дает доступ к этому чату. Храните ее в секрете.')
    } catch {
      setError('Не удалось получить ссылку. Попробуйте ещё раз.')
    }
  }

  useEffect(() => {
    if (!userId || !claimable.length || claimDismissed === userId) return

    const modal = Modal.confirm({
      title: 'Перенести гостевые чаты в аккаунт?',
      className: 'confirmModalLayout',
      okText: 'Перенести',
      cancelText: 'Не сейчас',
      content: (
        <>
          <p>Переписки останутся отдельными. После переноса доступ по гостевым ссылкам прекратится.</p>
          {claimable.map((chat) => (
            <p key={chat.id}>
              {chat.expert.authorName} · {chat.competenceName}
            </p>
          ))}
        </>
      ),
      onCancel: () => setClaimDismissed(userId),
      onOk: async () => {
        try {
          await guestChatAPI.claim()
          setClaimable([])
          notification.success('Гостевые чаты перенесены в раздел «Сообщения». Прежние ссылки отключены.')
        } catch (error) {
          notification.error('Не удалось перенести чаты. Попробуйте ещё раз.')
          throw error
        }
      },
    })
    return () => modal.destroy()
  }, [userId, claimable, claimDismissed])

  if (userId) return null

  const selected = chats.find((chat) => chat.id === active)
  const draftCompetence = choices.find((choice) => choice.id === details?.competence)
  const headerExpert =
    screen === 'chat'
      ? selected?.expert || draftCompetence?.experts.find((expert) => expert.id === details?.expert)
      : undefined
  const headerCompetence = selected?.competenceName || draftCompetence?.name
  const unread = chats.reduce((sum, chat) => sum + chat.unreadCount, 0)
  const panelHeight = window.innerWidth <= 575 ? availableHeight : Math.min(640, availableHeight)
  return (
    <aside className={styles.widget} style={{ bottom, maxHeight: availableHeight }}>
      {!open && invitation && (
        <div className={styles.invitation}>
          <button className={styles.close} aria-label="Скрыть приглашение" onClick={() => setInvitation(false)}>
            <Close />
          </button>
          <span className={styles.headset}>
            <HeadsetIcon />
          </span>
          <div>
            <strong>Главные эксперты</strong>
            <p>Есть вопрос о компетенции или чемпионате? Спросите главных экспертов</p>
          </div>
        </div>
      )}
      {open && (
        <section
          className={`${styles.panel} ${screen === 'form' ? styles.formPanel : ''}`}
          aria-label={headerExpert ? `Чат с ${headerExpert.authorName}` : 'Вопросы главному эксперту'}
          style={screen === 'form' ? undefined : { height: panelHeight, maxHeight: availableHeight }}
        >
          <header>
            {screen !== 'list' && (headerExpert || chats.length > 0) && (
              <button
                className={styles.backButton}
                aria-label="Назад к чатам"
                title="Мои чаты"
                onClick={() => setScreen('list')}
              >
                <BackwardIcon />
              </button>
            )}
            {headerExpert ? (
              <>
                <span className={styles.headerAvatar}>
                  <Avatar key={headerExpert.id} image={headerExpert.avatar} size="medium" />
                </span>
                <div className={styles.headerIdentity}>
                  <strong title={headerExpert.authorName}>{headerExpert.authorName}</strong>
                  <small title={headerCompetence}>{headerCompetence}</small>
                </div>
              </>
            ) : (
              <>
                <span className={styles.headset} aria-hidden="true">
                  <HeadsetIcon />
                </span>
                <strong>Вопросы главному эксперту</strong>
              </>
            )}
            <div className={styles.headerActions}>
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                visible={actionsOpen}
                onVisibleChange={setActionsOpen}
                overlayClassName={styles.actionsMenu}
                overlay={
                  <Menu
                    onClick={({ key }) => {
                      setActionsOpen(false)
                      if (key === 'link') void copyLink()
                      else {
                        setScreen('form')
                        setActive(undefined)
                        setDetails(undefined)
                        if (chats[0]?.name) form.setFieldsValue({ name: chats[0].name })
                      }
                    }}
                  >
                    <Menu.Item key="link" disabled={screen !== 'chat' || !selected}>
                      Ссылка на чат
                    </Menu.Item>
                    <Menu.Item key="question">Задать вопрос</Menu.Item>
                  </Menu>
                }
              >
                <button
                  className={styles.menuButton}
                  aria-label="Действия с чатом"
                  aria-haspopup="menu"
                  aria-expanded={actionsOpen}
                >
                  <More className={styles.moreIcon} size={20} color="#818C99" />
                </button>
              </Dropdown>
              <button
                className={styles.close}
                aria-label="Свернуть чат"
                onClick={() => {
                  setOpen(false)
                  setInvitation(false)
                }}
              >
                <Close />
              </button>
            </div>
          </header>
          {error && (
            <div role="alert" className={styles.error}>
              {error}
              <button
                onClick={() => {
                  void refresh()
                    .then(async () => {
                      await recover()
                      if (!userId) setChoices(await guestChatAPI.choices())
                      setError('')
                    })
                    .catch(() => setError('Не удалось загрузить чаты. Попробуйте ещё раз.'))
                  if (active) void load(active, true)
                }}
              >
                Повторить
              </button>
            </div>
          )}
          {screen === 'form' && (
            <div className={`${styles.body} ${styles.formBody}`}>
              <p className={`${styles.bubble} ${styles.intro}`}>{greeting}</p>
              <strong className={styles.formTitle}>Кому задать вопрос и как к вам обращаться?</strong>
              <Form form={form} layout="vertical" onFinish={start}>
                <Form.Item
                  name="competence"
                  label="Компетенция"
                  rules={[{ required: true, message: 'Выберите компетенцию' }]}
                >
                  <Select
                    placeholder="Выберите компетенцию"
                    suffixIcon={<ChevronIcon />}
                    value={competence}
                    onChange={(value) => {
                      setCompetence(value)
                      form.setFieldsValue({ expert: undefined })
                    }}
                    dropdownClassName={styles.dropdown}
                    getPopupContainer={() => document.body}
                  >
                    {choices.map((choice) => (
                      <Option key={choice.id} value={String(choice.id)}>
                        {choice.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="expert"
                  label="Главный эксперт"
                  rules={[{ required: true, message: 'Выберите эксперта' }]}
                >
                  <Select
                    placeholder="Выберите главного эксперта"
                    suffixIcon={<ChevronIcon />}
                    dropdownClassName={styles.dropdown}
                    getPopupContainer={() => document.body}
                  >
                    {choices
                      .find((choice) => String(choice.id) === competence)
                      ?.experts.map((expert) => (
                        <Option key={expert.id || 0} value={String(expert.id)}>
                          {expert.authorName}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="name"
                  label="Ваше имя"
                  rules={[{ required: true, whitespace: true, max: 150, message: 'Введите имя (до 150 символов)' }]}
                >
                  <Input maxLength={150} />
                </Form.Item>
                <Form.Item
                  name="consent"
                  valuePropName="checked"
                  rules={[
                    { validator: (_, value) => (value ? Promise.resolve() : Promise.reject('Необходимо согласие')) },
                  ]}
                >
                  <Checkbox>
                    Соглашаюсь с <Link to="/confidentiality-policy">политикой обработки персональных данных</Link>
                  </Checkbox>
                </Form.Item>
                <Button className={styles.startButton} htmlType="submit" type="primary">
                  Начать чат
                </Button>
              </Form>
            </div>
          )}
          {screen === 'list' && (
            <div className={styles.body}>
              {!chats.length && <Empty description="Чатов с экспертами не создано" />}
              {chats.map((chat) => (
                <button
                  key={chat.id}
                  className={styles.chatItem}
                  onClick={() => {
                    setActive(chat.id)
                    setScreen('chat')
                    setError('')
                  }}
                >
                  <Avatar image={chat.expert.avatar} size="medium" />
                  <span>
                    <strong>{chat.expert.authorName}</strong>
                    <small>{chat.competenceName}</small>
                    <small>{chat.lastMessage}</small>
                  </span>
                  {!!chat.unreadCount && <b className={styles.badge}>{chat.unreadCount}</b>}
                </button>
              ))}
            </div>
          )}
          {screen === 'chat' && (
            <>
              {!connected && active && <small className={styles.connection}>Восстанавливаем соединение…</small>}
              {!!pins.length && (
                <details className={styles.pin}>
                  <summary>Закрепленные сообщения — {pins.length}</summary>
                  {pins.map((pin) => (
                    <p key={pin.id}>
                      <strong>{pin.user?.authorName}</strong>: {pin.message}
                    </p>
                  ))}
                </details>
              )}
              <div className={`${styles.body} ${styles.history}`} ref={scrollRef} aria-live="polite">
                {older && (
                  <button
                    disabled={historyLoading}
                    onClick={async () => {
                      if (!active) return
                      setHistoryLoading(true)
                      const id = active
                      const height = scrollRef.current?.scrollHeight || 0
                      try {
                        const history = await guestChatAPI.messages(id, older)
                        if (current.current.active !== id) return
                        setItems((previous) => [...history.results, ...previous])
                        setOlder(history.hasOlder ? history.olderCursor : null)
                        requestAnimationFrame(() => {
                          if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight - height
                        })
                      } catch {
                        setError('Не удалось загрузить предыдущие сообщения.')
                      } finally {
                        setHistoryLoading(false)
                      }
                    }}
                  >
                    Показать предыдущие сообщения
                  </button>
                )}
                <Spin spinning={historyLoading} />
                <div className={styles.historySpacer} aria-hidden="true" />
                {(!active || (!older && !historyLoading && selected)) && (
                  <div className={`${styles.message} ${styles.systemMessage}`}>
                    <p className={styles.bubble}>
                      {selected?.name || details?.name}, рады знакомству! Задайте вопрос — передадим его главному
                      эксперту.
                    </p>
                    <small>{messageTime(items[0]?.created || greetingAt)}</small>
                  </div>
                )}
                {items.map((item, index) => (
                  <div key={item.id} className={`${styles.message} ${item.user?.isGuest ? styles.own : ''}`}>
                    {!item.user?.isGuest &&
                      (index === 0 ||
                        items[index - 1]?.user?.isGuest ||
                        items[index - 1]?.user?.id !== item.user?.id) && (
                        <span className={styles.sender}>{item.user ? 'Главный эксперт' : 'Пользователь удален'}</span>
                      )}
                    <div className={styles.bubble}>
                      {item.parent && (
                        <blockquote>
                          <strong>{item.parent.user?.authorName}</strong>
                          <p>{item.parent.message}</p>
                        </blockquote>
                      )}
                      <p>{item.message}</p>
                    </div>
                    <small>
                      {messageTime(item.created, items[index - 1]?.created)}
                      {item.isEdited ? ' · изменено' : ''}
                    </small>
                  </div>
                ))}
              </div>
              <div
                className={`${styles.composer} ${
                  !selected?.isClosed && (active || !captchaVisible) ? styles.messageComposer : ''
                }`}
              >
                {selected?.isClosed ? (
                  <div className={styles.completed}>Главный эксперт завершил чат</div>
                ) : (
                  <>
                    <div
                      className={`${styles.captchaHint} ${!active && captchaVisible ? styles.captchaHintVisible : ''}`}
                      aria-hidden={!!active || !captchaVisible}
                    >
                      <div className={styles.captchaHintInner}>
                        <p>Для отправки введите код с картинки</p>
                      </div>
                    </div>
                    <div className={styles.composeRow}>
                      {!active && captchaVisible ? (
                        <Form
                          key={socketRevision}
                          form={captchaForm}
                          preserve={false}
                          className={styles.captcha}
                          onValuesChange={(values) => setCaptchaValue(values.captchaValue || '')}
                          onFinish={send}
                        >
                          <Form.Captcha
                            form={captchaForm}
                            inline
                            disabled={busy}
                            onRefresh={() => {
                              setCaptchaValue('')
                              setError('')
                            }}
                            rules={[{ required: true, whitespace: true, message: 'Введите код' }]}
                          />
                        </Form>
                      ) : (
                        <Input.TextArea
                          aria-label="Сообщение эксперту"
                          placeholder="Напишите сообщение"
                          value={text}
                          maxLength={1000}
                          disabled={busy}
                          autoSize={{ minRows: 1, maxRows: 4 }}
                          onChange={(event) => setText(event.target.value)}
                        />
                      )}
                      <button
                        className={styles.sendButton}
                        aria-label="Отправить сообщение"
                        type="button"
                        disabled={busy || !text.trim() || (!active && captchaVisible && !captchaValue.trim())}
                        onClick={send}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="m3.4 3.2 17 7.9a1 1 0 0 1 0 1.8l-17 7.9a.7.7 0 0 1-1-.8l2-6.7L14 12l-9.6-1.3-2-6.7a.7.7 0 0 1 1-.8Z" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </section>
      )}
      {!open && (
        <button
          className={styles.launcher}
          aria-label="Открыть чат с главным экспертом"
          onClick={() => {
            setOpen(true)
            setInvitation(false)
          }}
        >
          <svg width="27" height="27" viewBox="0 0 32 32" aria-hidden="true">
            <path
              fill="currentColor"
              d="M16 4C8.8 4 3 9 3 15c0 2.4.9 4.6 2.4 6.4L3 28l7.4-2.9c1.7.6 3.6.9 5.6.9 7.2 0 13-5 13-11S23.2 4 16 4Z"
            />
          </svg>
          {!!unread && <b className={styles.badge}>{unread}</b>}
        </button>
      )}
    </aside>
  )
}

export default GuestChatWidget
